import { Container, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material';
import ContextualInfo from 'Components/ContextualInfo';
import useTranslate from 'Hooks/useTranslate';
import { Helmet } from 'react-helmet';
import Skeleton from 'Theme/components/Skeleton';
import TableGroupedHeader, { GroupedColumn, HeaderGroup } from 'Theme/components/TableCollection/TableGroupedHeader';

interface Props {
  loading: boolean;
  domains: IOrganizationDomain[];
  totalCount: number;
  currentPage: number;
  onChangePage: (page: number) => void;
  rowsPerPage: number;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  onHandleToggle: (row: IOrganizationDomain, field: 'forceTotp' | 'forceEmail') => void;
}

function OrganizationSecurity({
  loading,
  domains,
  totalCount,
  currentPage,
  onChangePage,
  rowsPerPage,
  onChangeRowsPerPage,
  onHandleToggle
}: Readonly<Props>) {
  const ts = useTranslate();

  const headerGroups: HeaderGroup[] = [
    { id: 'domain', label: ts('page.organizationSecurity.domainColumn'), rowSpan: 2 },
    { id: 'twoFactor', label: ts('page.organizationSecurity.twoFactorColumn'), colSpan: 2, align: 'center' }
  ];

  const createTwoFactorColumn = (field: 'forceTotp' | 'forceEmail', labelKey: string): GroupedColumn<IOrganizationDomain> => ({
    key: field,
    width: 0.2,
    align: 'center',
    renderHead: () => (
      <Stack direction="row" alignItems="center" justifyContent="center" lineHeight={2}>
        {labelKey}
        <ContextualInfo content={ts('page.organizationSecurity.twoFactorInfo')} />
      </Stack>
    ),
    renderSkeleton: () => <Skeleton variant="rectangular" width="50%" />,
    renderCell: ({ row }) => (
      <Tooltip title={row.isPublished ? ts('page.organizationSecurity.publishedDomainTooltip') : ''} arrow>
        <span>
          <Switch
            checked={row[field]}
            color="primary"
            disabled={row.isPublished}
            onChange={() => onHandleToggle(row, field)}
          />
        </span>
      </Tooltip>
    )
  });

  const columns: GroupedColumn<IOrganizationDomain>[] = [
    {
      key: 'name',
      width: 0.6,
      renderHead: () => ts('page.organizationSecurity.domainColumn'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="30%" />,
      renderCell: ({ row }) => (
        <Typography variant="body2">{row.name}</Typography>
      )
    },
    createTwoFactorColumn('forceTotp', ts('page.organizationSecurity.totpColumn')),
    createTwoFactorColumn('forceEmail', ts('page.organizationSecurity.emailColumn'))
  ];

  return (
    <>
      <Helmet title={ts('page.organizationSecurity.title')} />
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5" color="primary">
          {ts('page.organizationSecurity.title')}
        </Typography>
        <Paper>
          <TableGroupedHeader<IOrganizationDomain>
            loading={loading}
            headerGroups={headerGroups}
            columns={columns}
            rows={domains}
            getRowId={(row) => row.name}
            totalCount={totalCount}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
          />
        </Paper>
      </Container>
    </>
  );
}

export default OrganizationSecurity;
